import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Ticket, Calendar, MapPin, CreditCard, Share2, Download, Users } from 'lucide-react';
import NearULogo from '../components/NearULogo';
import { QRCodeCanvas } from 'qrcode.react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_51SVCSo382aFGgdDTM8fb8m063DuKfpCwaW8SSpAZ3KBkKMkGeEVhUkMUeA22P7hQwe04Bxs5eqRx0NhhAjdS87Qm001YK7K9gw');

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: { color: '#E6D7C3', fontFamily: '"Helvetica Neue", Helvetica, sans-serif', fontSmoothing: 'antialiased', fontSize: '16px', '::placeholder': { color: '#8A7355' } },
    invalid: { color: '#fa755a', iconColor: '#fa755a' },
  },
};

function PaymentForm({ ticketData, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/events/purchase`, ticketData);
      const result = await stripe.confirmCardPayment(data.client_secret, { payment_method: { card: elements.getElement(CardElement) } });
      if (result.error) {
        setError(result.error.message);
      } else {
        const confirmRes = await axios.post(`${API}/events/confirm-payment/${data.transaction_id}`);
        onSuccess(confirmRes.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: '24px' }}>
      <div style={{ background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </div>
      {error && <div style={{ padding: '12px', background: 'rgba(220, 38, 38, 0.1)', border: '1px solid rgba(220, 38, 38, 0.3)', borderRadius: '8px', color: '#fca5a5', marginBottom: '16px', fontSize: '14px' }}>{error}</div>}
      <button type="submit" disabled={!stripe || processing} style={{ width: '100%', padding: '16px', background: processing ? '#6B5635' : 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)', color: '#2C1B13', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 'bold', cursor: processing ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
        <CreditCard size={20} />
        {processing ? 'Processing...' : `Pay $${ticketData.totalAmount}`}
      </button>
    </form>
  );
}

const SendEventPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [formData, setFormData] = useState({ ticketType: '', numTickets: 1, recipientType: 'qr', recipientMember: '', recipientInfo: '', message: '', ticketPrice: 0, totalAmount: 0 });
  const [loading, setLoading] = useState(false);
  const [qrData, setQrData] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/events/search?city=Miami`);
      setEvents(data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEventSelect = (event) => {
    setSelectedEvent(event);
    setStep(2);
  };

  const handleTicketTypeSelect = (ticketType) => {
    const total = ticketType.price * formData.numTickets;
    setFormData({ ...formData, ticketType: ticketType.type, ticketPrice: ticketType.price, totalAmount: total });
  };

  const handleNumTicketsChange = (num) => {
    const total = formData.ticketPrice * num;
    setFormData({ ...formData, numTickets: num, totalAmount: total });
  };

  const handlePaymentSuccess = (data) => {
    setQrData(data);
    setStep(4);
  };

  const downloadQRCode = () => {
    const canvas = document.getElementById('qr-code-canvas');
    const url = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.download = `event-qr-${qrData.unique_code}.png`;
    link.href = url;
    link.click();
  };

  const shareQRCode = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: qrData.event_name, text: `I got you tickets! ${formData.message}`, url: qrData.qr_url });
      } catch (err) { console.error('Error sharing:', err); }
    } else {
      navigator.clipboard.writeText(qrData.qr_url);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div style={{ paddingBottom: '80px', minHeight: '100vh', background: 'linear-gradient(180deg, #0D0D0D 0%, #2A1B0E 100%)' }}>
      <div className="page-header">
        <button onClick={() => step === 1 ? navigate('/gifts') : setStep(step - 1)} style={{ background: 'none', border: 'none', color: '#E6D7C3', cursor: 'pointer', padding: '8px' }}>
          <ArrowLeft size={24} />
        </button>
        <div style={{ marginBottom: '12px' }}><NearULogo size="small" showText={true} /></div>
        <h1 className="page-title" style={{ fontFamily: 'Playfair Display, serif' }}>{step === 4 ? 'TICKETS SENT!' : 'EVENT TICKETS'}</h1>
      </div>

      <div style={{ padding: '0 20px' }}>
        {step === 1 && (
          <div>
            <h2 style={{ fontSize: '24px', color: '#E6D7C3', marginBottom: '20px', fontFamily: 'Playfair Display, serif' }}>Miami Events</h2>
            {loading ? (
              <p style={{ color: '#A57C33' }}>Loading events...</p>
            ) : (
              <div style={{ display: 'grid', gap: '16px' }}>
                {events.map(event => (
                  <div key={event.id} onClick={() => handleEventSelect(event)} style={{ background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '12px', padding: '16px', cursor: 'pointer', transition: 'all 0.3s ease' }} onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(230, 215, 195, 0.1)'} onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(230, 215, 195, 0.05)'}>
                    <h3 style={{ fontSize: '18px', color: '#E6D7C3', marginBottom: '8px', fontWeight: 'bold' }}>{event.name}</h3>
                    <p style={{ fontSize: '14px', color: '#A57C33', marginBottom: '4px' }}>
                      <Calendar size={14} style={{ display: 'inline', marginRight: '4px' }} />
                      {event.date} at {event.time}
                    </p>
                    <p style={{ fontSize: '14px', color: '#A57C33', marginBottom: '8px' }}>
                      <MapPin size={14} style={{ display: 'inline', marginRight: '4px' }} />
                      {event.venue}
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ padding: '4px 8px', background: 'rgba(159, 184, 212, 0.2)', color: '#9FB8D4', borderRadius: '4px', fontSize: '12px', textTransform: 'capitalize' }}>{event.category}</span>
                      <span style={{ fontSize: '16px', color: '#D7B56D', fontWeight: 'bold' }}>{event.price_range}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 2 && selectedEvent && (
          <div>
            <div style={{ background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
              <p style={{ color: '#A57C33', fontSize: '14px', marginBottom: '4px' }}>Selected Event</p>
              <h3 style={{ color: '#E6D7C3', fontSize: '18px', fontWeight: 'bold' }}>{selectedEvent.name}</h3>
              <p style={{ color: '#8A7355', fontSize: '14px' }}>{selectedEvent.date} • {selectedEvent.venue}</p>
            </div>
            <h2 style={{ fontSize: '24px', color: '#E6D7C3', marginBottom: '20px', fontFamily: 'Playfair Display, serif' }}>Select Tickets</h2>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#A57C33', marginBottom: '8px', fontSize: '14px' }}>Ticket Type</label>
              {selectedEvent.ticketTypes.map(ticket => (
                <div key={ticket.type} onClick={() => handleTicketTypeSelect(ticket)} style={{ background: formData.ticketType === ticket.type ? 'rgba(215, 181, 109, 0.15)' : 'rgba(230, 215, 195, 0.05)', border: `1px solid ${formData.ticketType === ticket.type ? '#D7B56D' : '#A57C33'}`, borderRadius: '8px', padding: '16px', marginBottom: '12px', cursor: 'pointer', transition: 'all 0.3s ease' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#E6D7C3', fontWeight: 'bold' }}>{ticket.type}</span>
                    <span style={{ color: '#D7B56D', fontWeight: 'bold', fontSize: '18px' }}>${ticket.price}</span>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', color: '#A57C33', marginBottom: '8px', fontSize: '14px' }}>Number of Tickets</label>
              <input type="number" min="1" max="10" value={formData.numTickets} onChange={(e) => handleNumTicketsChange(parseInt(e.target.value))} style={{ width: '100%', padding: '12px', background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '8px', color: '#E6D7C3', fontSize: '16px', outline: 'none' }} />
            </div>
            {formData.ticketType && (
              <div style={{ background: 'rgba(215, 181, 109, 0.1)', border: '1px solid #D7B56D', borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#A57C33' }}>Total:</span>
                  <span style={{ color: '#D7B56D', fontWeight: 'bold', fontSize: '20px' }}>${formData.totalAmount}</span>
                </div>
              </div>
            )}
            <button onClick={() => setStep(3)} disabled={!formData.ticketType} style={{ width: '100%', padding: '16px', background: !formData.ticketType ? '#6B5635' : 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)', color: '#2C1B13', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 'bold', cursor: !formData.ticketType ? 'not-allowed' : 'pointer' }}>Continue</button>
          </div>
        )}

        {step === 3 && (
          <div>
            <h2 style={{ fontSize: '24px', color: '#E6D7C3', marginBottom: '20px', fontFamily: 'Playfair Display, serif' }}>Delivery Details</h2>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#A57C33', marginBottom: '12px', fontSize: '14px' }}>Send To</label>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                <button type="button" onClick={() => setFormData({...formData, recipientType: 'qr'})} style={{ flex: 1, padding: '12px', background: formData.recipientType === 'qr' ? 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)' : 'rgba(230, 215, 195, 0.05)', color: formData.recipientType === 'qr' ? '#2C1B13' : '#E6D7C3', border: '1px solid #A57C33', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>Anyone (QR Code)</button>
                <button type="button" onClick={() => setFormData({...formData, recipientType: 'member'})} style={{ flex: 1, padding: '12px', background: formData.recipientType === 'member' ? 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)' : 'rgba(230, 215, 195, 0.05)', color: formData.recipientType === 'member' ? '#2C1B13' : '#E6D7C3', border: '1px solid #A57C33', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>NearU Member</button>
              </div>
              {formData.recipientType === 'member' ? (
                <input type="text" placeholder="Enter member username" value={formData.recipientMember} onChange={(e) => setFormData({...formData, recipientMember: e.target.value})} style={{ width: '100%', padding: '12px', background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '8px', color: '#E6D7C3', fontSize: '16px', outline: 'none' }} />
              ) : (
                <input type="text" placeholder="Recipient name (optional)" value={formData.recipientInfo} onChange={(e) => setFormData({...formData, recipientInfo: e.target.value})} style={{ width: '100%', padding: '12px', background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '8px', color: '#E6D7C3', fontSize: '16px', outline: 'none' }} />
              )}
            </div>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', color: '#A57C33', marginBottom: '8px', fontSize: '14px' }}>Personal Message</label>
              <textarea placeholder="Add a message..." value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} rows="3" style={{ width: '100%', padding: '12px', background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '8px', color: '#E6D7C3', fontSize: '16px', outline: 'none', resize: 'vertical' }} />
            </div>
            <Elements stripe={stripePromise}>
              <PaymentForm ticketData={{ ...formData, eventName: selectedEvent.name, eventDate: selectedEvent.date, eventTime: selectedEvent.time, eventVenue: selectedEvent.venue }} onSuccess={handlePaymentSuccess} />
            </Elements>
          </div>
        )}

        {step === 4 && qrData && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ width: '80px', height: '80px', background: 'linear-gradient(135deg, #9FB8D4 0%, #7A9BBF 100%)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
              <Ticket size={40} color="#2C1B13" />
            </div>
            <h2 style={{ fontSize: '28px', color: '#E6D7C3', marginBottom: '12px', fontFamily: 'Playfair Display, serif' }}>Tickets Sent!</h2>
            <p style={{ color: '#A57C33', marginBottom: '32px', fontSize: '16px' }}>Present QR code at the event</p>
            <div style={{ background: 'white', padding: '24px', borderRadius: '16px', display: 'inline-block', marginBottom: '24px' }}>
              <QRCodeCanvas id="qr-code-canvas" value={qrData.qr_url} size={250} level="H" />
            </div>
            <div style={{ background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '12px', padding: '16px', marginBottom: '24px', textAlign: 'left' }}>
              <p style={{ color: '#A57C33', fontSize: '14px', marginBottom: '8px' }}>Ticket Details</p>
              <h3 style={{ color: '#E6D7C3', fontSize: '18px', fontWeight: 'bold', marginBottom: '4px' }}>{qrData.event_name}</h3>
              <p style={{ color: '#D7B56D' }}>{qrData.event_date} at {qrData.event_time}</p>
              <p style={{ color: '#E6D7C3', marginTop: '4px' }}>
                <Users size={14} style={{ display: 'inline', marginRight: '4px' }} />
                {qrData.num_tickets} {qrData.num_tickets === 1 ? 'ticket' : 'tickets'}
              </p>
              <p style={{ color: '#8A7355', fontSize: '14px', marginTop: '8px' }}>Code: {qrData.unique_code}</p>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <button onClick={shareQRCode} style={{ flex: 1, padding: '14px', background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)', color: '#2C1B13', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Share2 size={20} /> Share
              </button>
              <button onClick={downloadQRCode} style={{ flex: 1, padding: '14px', background: 'rgba(230, 215, 195, 0.1)', color: '#E6D7C3', border: '1px solid #A57C33', borderRadius: '12px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Download size={20} /> Download
              </button>
            </div>
            <button onClick={() => navigate('/gifts')} style={{ width: '100%', padding: '14px', background: 'none', color: '#A57C33', border: 'none', fontSize: '16px', cursor: 'pointer' }}>Back to Gifts</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SendEventPage;